import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { User } from '@/api/entities';
import { ArrowDown } from 'lucide-react';

export default function AdCapital() {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      const user = await User.getCurrentUser();

      if (user) {
        const roles = user.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin') || user.role === 'admin';

        if (!isAdmin) {
          navigate('/');
          return;
        }

        setIsLoading(false);
      } else {
        setIsLoading(false);
        navigate('/');
      }
    };

    loadUser();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading…</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Defera Capital — Announcement</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <style>{`
          @keyframes arrowFloat {
            0%, 100% { transform: translateY(0); opacity: 0.85; }
            50% { transform: translateY(6px); opacity: 1; }
          }

          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <div
          className="relative overflow-hidden rounded-3xl mx-auto bg-black"
          style={{
            width: '432px',
            height: '768px',
            aspectRatio: '9 / 16',
            maxWidth: '432px',
            maxHeight: '768px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >

          {/* Content — fully left-aligned */}
          <div className="relative z-10 h-full flex flex-col items-start px-8 py-12">

            {/* 1. Defera logo at top — left aligned */}
            <div style={{ animation: 'fadeUp 0.7s ease-out 0.05s both' }}>
              <img
                src="/defera-logo-web.png"
                alt="Defera"
                className="h-16 object-contain"
              />
            </div>

            {/* 2. Arrow pointing down — left aligned */}
            <div
              className="my-7"
              style={{ animation: 'fadeUp 0.7s ease-out 0.15s both' }}
            >
              <div style={{ animation: 'arrowFloat 2.4s ease-in-out infinite' }}>
                <ArrowDown
                  className="w-6 h-6 text-white/70"
                  strokeWidth={1.25}
                />
              </div>
            </div>

            {/* 3. Defera logo + CAPITAL lockup — CAPITAL sits bottom-right under the wordmark */}
            <div
              className="relative"
              style={{ animation: 'fadeUp 0.8s ease-out 0.25s both' }}
            >
              <img
                src="/defera-logo-web.png"
                alt="Defera"
                className="h-20 object-contain"
              />
              {/* CAPITAL anchored to the bottom-right of the logo */}
              <div
                className="absolute text-white/90"
                style={{
                  fontFamily: '"Inter", "SF Pro Text", ui-sans-serif, system-ui, sans-serif',
                  fontSize: '0.6rem',
                  fontWeight: 500,
                  letterSpacing: '0.5em',
                  textTransform: 'uppercase',
                  bottom: '2px',
                  right: '-2px',
                }}
              >
                Capital
              </div>
            </div>

            {/* 4. Press-release statement — left aligned */}
            <div
              className="flex flex-col items-start mt-12"
              style={{ animation: 'fadeUp 0.9s ease-out 0.4s both' }}
            >
              <h1
                className="text-white font-semibold tracking-tight leading-[1.2]"
                style={{
                  fontFamily: '"Inter", "SF Pro Display", ui-sans-serif, system-ui, sans-serif',
                  fontSize: '1.45rem',
                  fontWeight: 600,
                }}
              >
                Defera brings all
                <br />
                financing in-house.
              </h1>

              <span
                className="block uppercase font-medium mt-8 mb-2"
                style={{
                  fontFamily: '"Inter", "SF Pro Text", ui-sans-serif, system-ui, sans-serif',
                  letterSpacing: '0.2em',
                  fontSize: '0.67rem',
                  color: 'rgba(255,255,255,0.2)',
                }}
              >
                May 15, 2026
              </span>
              <p
                className="text-white/65 text-left"
                style={{
                  fontFamily: '"Inter", "SF Pro Text", ui-sans-serif, system-ui, sans-serif',
                  fontSize: '0.88rem',
                  lineHeight: 1.7,
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                }}
              >
                <span className="text-white font-medium">Defera</span> has
                established{' '}
                <span className="text-white font-medium">Defera Capital</span>{' '}
                as its dedicated financial vehicle, following a recent
                six-figure capital raise. The move transitions all
                consumer credit operations in-house, ending the company's
                reliance on external lending partners and giving Defera direct
                control over its financial products.                 With Defera Capital in place, the company is now positioned
                to move faster, price more competitively, and operate with
                full autonomy over its credit decisions.
              </p>
            </div>

            {/* Footer — DEFERA.APP */}
            <div
              className="mt-auto"
              style={{ animation: 'fadeUp 1s ease-out 0.55s both' }}
            >
              <span
                className="text-white/35 text-[0.72rem]"
                style={{
                  fontFamily: '"Inter", "SF Pro Text", ui-sans-serif, system-ui, sans-serif',
                  letterSpacing: '0.25em',
                  fontWeight: 400,
                }}
              >
                DEFERA.APP
              </span>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
